import { Column } from 'further-ui/components/Layout';
import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { TrancheFormInput } from '../../useTrancheForm';
import FormField from 'further-ui/components/FormField';
import BasicSelect from 'further-ui/components/BasicSelect';
import Switch from 'further-ui/components/Switch';
import NumberInput from 'further-ui/components/NumberInput';
import DatePicker from 'further-ui/components/DatePicker';
import { TrancheVisibility } from 'further-types/fund';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { TextField, Typography } from '@mui/material';
import { common } from '@mui/material/colors';
import { FirmSelector } from 'components/FirmSelector';
import { useGetRole } from 'hooks/ui/useGetRole';
import { EisTag } from '../../types';
import { DASHBOARD_MY_FIRM } from 'adminConstants/routes';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import TypographyButton from 'further-ui/components/TypographyButton';
import { TrancheDotColor } from '../dotColors';
import { fundStatusOptions, getHiddenFundStatuses } from '../statusOptions';

type Props = {
  control: Control<TrancheFormInput>;
  tagList: Array<EisTag>;
  currency: CurrencyCode;
  isCreating: boolean;
  hasInvestments: boolean;
  showDraftDots: boolean;
};

const GeneralInformation: React.FC<Props> = ({
  control,
  tagList,
  currency,
  isCreating,
  hasInvestments,
  showDraftDots,
}) => {
  const draftDotColor = showDraftDots ? TrancheDotColor.Draft : undefined;
  const { firmId: usersFirmId } = useGetRole();
  const hiddenStatuses = getHiddenFundStatuses(isCreating, hasInvestments);
  const availableFundStatusOptions = fundStatusOptions.filter(
    ({ value }) => !hiddenStatuses.includes(value),
  );
  const fundTypeOptions = tagList.map((tag) => ({
    value: tag.id,
    label: tag.name,
  }));

  return (
    <Column spacing="lg" fullWidth>
      <FormField label="Firm" size="md" dotColor={draftDotColor}>
        <Controller
          control={control}
          name="firmId"
          render={({ field, fieldState }) => (
            <FirmSelector
              firmId={field.value}
              onChange={field.onChange}
              placeholder="Select firm"
              allowDeselection={false}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              required
              disabled={!!usersFirmId}
            />
          )}
        />
      </FormField>
      <FormField label="Tranche status" size="md">
        <Controller
          control={control}
          name="fundStatus"
          render={({ field }) => (
            <BasicSelect
              fullWidth
              value={field.value}
              size="small"
              onChange={field.onChange}
              options={availableFundStatusOptions}
            />
          )}
        />
      </FormField>
      <FormField label="Tranche name" size="md" dotColor={draftDotColor}>
        <Controller
          control={control}
          name="fundName"
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              size="small"
              fullWidth
              placeholder="Enter tranche name"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </FormField>

      <FormField
        extraInformation="Hide this tranche if you want it to be viewable by investors only via a direct link."
        label="Hide from public portal subscription page"
        size="md"
        labelSize="lg"
        layout="horizontal"
      >
        <Controller
          control={control}
          name="trancheVisibility"
          render={({ field }) => (
            <Switch
              checked={field.value === TrancheVisibility.ShowOnTranche}
              onChange={(checked) =>
                field.onChange(
                  checked
                    ? TrancheVisibility.ShowOnTranche
                    : TrancheVisibility.ShowOnFirm,
                )
              }
            />
          )}
        />
      </FormField>
      <FormField
        label="Fund type"
        size="md"
        dotColor={draftDotColor}
        tooltip={
          <Column spacing="xs">
            <Typography variant="inherit">
              Select the type of investment(s) being made with this tranche:
            </Typography>
            <ColorIndicatorWithText
              color={common.white}
              size={6}
              alignItems="flex-start"
              text={
                <>
                  For mixed EIS and SEIS funds, select <b>EIS</b>.
                </>
              }
            />
            <ColorIndicatorWithText
              size={6}
              color={common.white}
              alignItems="flex-start"
              text={
                <>
                  For direct investments <i>with tax relief</i> that you want to
                  display individually on client portals, select <b>EIS</b> or{' '}
                  <b>SEIS</b> (as applicable).
                </>
              }
            />
            <ColorIndicatorWithText
              color={common.white}
              size={6}
              alignItems="flex-start"
              text={
                <>
                  For direct investments <i>with no tax relief</i> that you want
                  to display individually on client portals, select{' '}
                  <b>Direct</b>.
                </>
              }
            />
            <ColorIndicatorWithText
              color={common.white}
              size={6}
              alignItems="flex-start"
              text={
                <>
                  For direct investments <i>with or without tax relief</i> you
                  want to display grouped: (i) by tax year, (ii) by calendar
                  year or (iii) all as one on client portals (setting adjustable
                  in your{' '}
                  <TypographyButton
                    text="My Firm"
                    href={DASHBOARD_MY_FIRM}
                    target="_blank"
                    rel="noreferrer"
                  />{' '}
                  page), select <b>Syndicate</b>.
                </>
              }
            />
          </Column>
        }
      >
        <Controller
          control={control}
          name="tag"
          render={({ field, fieldState }) => (
            <BasicSelect
              fullWidth
              size="small"
              placeholder="Select fund type"
              value={field.value}
              onChange={field.onChange}
              options={fundTypeOptions}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </FormField>
      <FormField
        label="Professional clients only"
        size="md"
        labelSize="lg"
        layout="horizontal"
      >
        <Controller
          control={control}
          name="isProfessionalClientsOnly"
          render={({ field }) => (
            <Switch checked={field.value} onChange={field.onChange} />
          )}
        />
      </FormField>
      <FormField
        label="Fund raise limit"
        size="md"
        tooltip="Set an amount at which the tranche will be automatically closed."
      >
        <Controller
          control={control}
          name="fundRaiseLimit"
          render={({ field }) => (
            <NumberInput
              fullWidth
              size="small"
              value={field.value}
              onChange={field.onChange}
              placeholder="Enter amount"
              currency={currency}
              currencyStartAdornment
            />
          )}
        />
      </FormField>
      <FormField label="Start date" size="md">
        <Controller
          control={control}
          name="startDate"
          render={({ field }) => (
            <DatePicker
              fullWidth
              placeholder="Select date"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </FormField>
      <FormField
        label="Scheduled close date"
        size="md"
        dotColor={draftDotColor}
      >
        <Controller
          control={control}
          name="finishDate"
          render={({ field, fieldState }) => (
            <DatePicker
              value={field.value}
              onChange={field.onChange}
              error={!!fieldState.error}
              fullWidth
              placeholder="Select date"
              helperText={fieldState.error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Close date" size="md">
        <Controller
          control={control}
          name="closedDate"
          render={({ field }) => (
            <DatePicker
              fullWidth
              placeholder="Select date"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </FormField>
    </Column>
  );
};

export default GeneralInformation;
